import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Segmented, Select, Tag } from 'antd';
import { Download, Plus, GitBranch, BarChart3, Network } from 'lucide-react';
import { PageHeader, Card } from '@/components/common/PageHeader';
import { useBreakpoint } from '@/hooks/useBreakpoint';

const ganttRows = [
  { id: 'p', label: 'Triển khai SIEM Q2', isParent: true, start: 0, duration: 14, progress: 60 },
  { id: '1', label: 'Khảo sát 10 nguồn log', start: 0, duration: 2 },
  { id: '2', label: 'Cài Wazuh Manager', start: 2, duration: 2 },
  { id: '3', label: 'Cấu hình 8 use case', start: 2, duration: 4 },
  { id: '4', label: 'Viết quy trình xử lý sự cố', start: 5, duration: 3, inProgress: true, progress: 60 },
  { id: '5', label: 'Đào tạo vận hành', start: 8, duration: 3, future: true },
];

export function TaskTreePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const [view, setView] = useState<string | number>(isMobile ? 'tree' : 'gantt');

  return (
    <>
      <PageHeader
        breadcrumb={`TASK / ${id ?? 'TSK-0341'}`}
        title='Cây công việc: "Triển khai SIEM Wazuh Q2"'
        extra={
          <>
            <Select defaultValue="week" style={{ width: 120 }} options={[
              { value: 'day', label: 'Theo ngày' },
              { value: 'week', label: 'Theo tuần' },
              { value: 'month', label: 'Theo tháng' },
            ]} />
            <Button icon={<Download size={14} />}>Xuất</Button>
            <Button type="primary" icon={<Plus size={14} />}>Thêm task con</Button>
          </>
        }
      />

      <div style={{ marginBottom: 16 }}>
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { value: 'gantt', label: <><BarChart3 size={12} style={{ verticalAlign: -1 }} /> Gantt</> },
            { value: 'tree', label: <><GitBranch size={12} style={{ verticalAlign: -1 }} /> Cây</> },
            { value: 'network', label: <><Network size={12} style={{ verticalAlign: -1 }} /> Sơ đồ mạng</> },
          ]}
        />
      </div>

      {view === 'gantt' && (
        <Card padding={0}>
          <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 720 }}>
          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', borderBottom: '1px solid #d4d9df', background: '#f6f7f8' }}>
            <div style={{ padding: '10px 12px', borderRight: '1px solid #d4d9df', fontSize: 12, fontWeight: 500, color: '#48535e' }}>
              Task
            </div>
            <div className="mono" style={{ display: 'grid', gridTemplateColumns: 'repeat(14,1fr)', padding: '10px 8px', fontSize: 11, color: '#7d8a98' }}>
              {Array.from({ length: 14 }, (_, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  {15 + i > 30 ? 15 + i - 30 : 15 + i}
                </div>
              ))}
            </div>
          </div>

          {/* Rows */}
          {ganttRows.map((row) => (
            <div
              key={row.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '280px 1fr',
                borderBottom: '1px solid #eceef1',
              }}
            >
              <div
                style={{
                  padding: '10px 12px',
                  borderRight: '1px solid #eceef1',
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  paddingLeft: row.isParent ? 12 : 28,
                  fontWeight: row.isParent ? 600 : 400,
                }}
              >
                {row.isParent ? '▼' : null}
                <span>{row.label}</span>
              </div>
              <div style={{ position: 'relative', height: 40, padding: '0 8px' }}>
                <div
                  style={{
                    position: 'absolute',
                    top: 13,
                    left: `${(row.start / 14) * 100}%`,
                    width: `${(row.duration / 14) * 100}%`,
                    height: 14,
                    borderRadius: 3,
                    background: row.future
                      ? '#adb6c0'
                      : row.isParent
                      ? '#6699CC'
                      : row.inProgress
                      ? '#41b6a7'
                      : '#41b6a7',
                    overflow: 'hidden',
                  }}
                >
                  {row.inProgress && row.progress && (
                    <div style={{ height: '100%', width: `${row.progress}%`, background: '#6699CC' }} />
                  )}
                </div>
              </div>
            </div>
          ))}

          <div style={{ padding: '8px 16px', background: '#f6f7f8', fontSize: 11, color: '#7d8a98' }}>
            Bar đậm = đã xong · Bar nhạt = chưa bắt đầu
          </div>
          </div>
          </div>
        </Card>
      )}

      {view === 'tree' && (
        <Card padding={20}>
          <div style={{ fontSize: 14, lineHeight: 2 }}>
            <div style={{ fontWeight: 600 }}>
              ◈ TSK-0341 · Triển khai SIEM Wazuh Q2 <Tag color="gold" style={{ marginLeft: 8 }}>60%</Tag>
            </div>
            <ul style={{ listStyle: 'none', paddingLeft: 24, borderLeft: '1px dashed #adb6c0', marginLeft: 6 }}>
              <li>✓ TSK-0341.1 · Khảo sát 10 nguồn log <span style={{ color: '#adb6c0' }}>· Trần Văn C</span></li>
              <li>✓ TSK-0341.2 · Cài Wazuh Manager server trung tâm <span style={{ color: '#adb6c0' }}>· Lê Văn F</span></li>
              <li>
                ✓ TSK-0341.3 · Cấu hình 8 use case <span style={{ color: '#adb6c0' }}>· Lê Văn F</span>
                <ul style={{ listStyle: 'none', paddingLeft: 24, borderLeft: '1px dashed #adb6c0', marginLeft: 6 }}>
                  <li>✓ Use case: Brute force SSH</li>
                  <li>✓ Use case: Abnormal DB query</li>
                  <li>✓ Use case: FIM HIS config files</li>
                  <li>✓ ... 5 use case khác</li>
                </ul>
              </li>
              <li>
                ◐ TSK-0341.4 · Viết quy trình xử lý sự cố <span style={{ color: '#adb6c0' }}>· Phạm Ngọc Ánh · 60%</span>
              </li>
              <li>○ TSK-0341.5 · Đào tạo vận hành team CNTT <span style={{ color: '#adb6c0' }}>· Nguyễn Văn D</span></li>
            </ul>
          </div>
        </Card>
      )}

      {view === 'network' && (
        <Card padding={40}>
          <div style={{ textAlign: 'center', color: '#7d8a98', fontSize: 14 }}>
            Sơ đồ mạng — chưa triển khai trong MVP này
          </div>
        </Card>
      )}
    </>
  );
}
